import React, { useEffect, useMemo, useState } from 'react';
import { profileFetchStatistics } from '../../services/api';
import TradingChart from './TradingChart';
import theme from '../../theme';
import {
  buildProfileChartSeries,
  calculateDateRange,
  formatAverageTimeInTrade,
  formatSymbolSelection,
  normalizeStatisticsResponse,
} from './profileStatistics';

const PERIODS = ['DAY', 'WEEK', 'MONTH', 'YTD'];
const INFINITE_METRIC_LABEL = 'Infinity';

const formatMetricNumber = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'N/A';
  }

  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const formatMetricValue = (value, displayValue = null, digits = 2) => {
  if (displayValue) {
    return displayValue;
  }

  return formatMetricNumber(value, digits);
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) {
    return '';
  }

  const normalized = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProfileKPIStatistic = ({ broker, account, symbols, symbolSectypes = [] }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('DAY');
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  const dateRange = useMemo(
    () => calculateDateRange(selectedPeriod),
    [selectedPeriod],
  );

  const chartSeries = useMemo(
    () => buildProfileChartSeries(statistics?.chartData),
    [statistics?.chartData],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!symbols || symbols.length === 0 || !dateRange.startDate || !dateRange.endDate) {
        if (isMounted) {
          setStatistics(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const response = await profileFetchStatistics(
          broker,
          account,
          symbols,
          symbolSectypes,
          dateRange.startDate,
          dateRange.endDate,
          0,
        );

        if (!isMounted) {
          return;
        }

        setStatistics(normalizeStatisticsResponse(response));
      } catch (error) {
        console.error('Error in fetchData ProfileKPIStatistic:', error);
        if (isMounted) {
          setStatistics(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [account, broker, dateRange.endDate, dateRange.startDate, symbolSectypes, symbols]);

  const symbolCount = symbols?.length || 0;
  const showNoDataMessage = !loading && !statistics;
  const symbolSelectionLabel = formatSymbolSelection(symbols);
  const metricCards = statistics
    ? [
        {
          label: 'Net PnL',
          value: formatMetricNumber(statistics.totalPnl),
          tone: statistics.totalPnl >= 0 ? 'text-emerald-600' : 'text-rose-600',
          caption: statistics.totalPnl >= 0 ? 'Realized gain in selected range' : 'Realized drawdown in selected range',
        },
        {
          label: 'Win/Loss Ratio',
          value: formatMetricValue(statistics.winLossRatio, statistics.winLossRatioDisplay),
          tone:
            statistics.winLossRatioDisplay === INFINITE_METRIC_LABEL ||
            (statistics.winLossRatio !== null && statistics.winLossRatio >= 1)
              ? 'text-emerald-600'
              : 'text-amber-600',
          caption: 'Winning closes per losing close',
        },
        {
          label: 'Profit Factor',
          value: formatMetricValue(statistics.profitFactor, statistics.profitFactorDisplay),
          tone:
            statistics.profitFactorDisplay === INFINITE_METRIC_LABEL ||
            (statistics.profitFactor !== null && statistics.profitFactor >= 1)
              ? 'text-emerald-600'
              : 'text-amber-600',
          caption: 'Gross profit divided by gross loss',
        },
        {
          label: 'Average Time In Trade',
          value: formatAverageTimeInTrade(statistics.avgTradeTime, statistics.completedTrades),
          tone: theme.textPrimary,
          caption: 'Weighted by matched quantity',
        },
      ]
    : [];

  return (
    <section className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} overflow-hidden shadow-sm`}>
      <div className="border-b border-olive-100 bg-gradient-to-r from-olive-50 via-white to-olive-50 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${theme.textSecondary}`}>
              Performance Overview
            </p>
            <h3 className={`mt-1 text-xl font-bold ${theme.textPrimary}`}>Profile Statistics</h3>
            <p className={`mt-1 text-sm ${theme.textMuted}`}>
              {broker?.toUpperCase()} account {account}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {PERIODS.map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => setSelectedPeriod(period)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold tracking-wide transition-all duration-200 ${
                  selectedPeriod === period
                    ? `${theme.btnPrimary} shadow-sm`
                    : `border border-olive-200 bg-white ${theme.textSecondary} hover:bg-olive-50`
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-olive-200 bg-white px-3 py-1 text-xs font-semibold text-olive-700">
              {symbolSelectionLabel}
            </span>
            <span className="rounded-full bg-olive-100 px-3 py-1 text-xs font-medium text-olive-600">
              {symbolCount} selected
            </span>
          </div>

          <p className={`text-xs font-medium ${theme.textMuted}`}>
            {formatDisplayDate(dateRange.startDate)} to {formatDisplayDate(dateRange.endDate)}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-6 xl:grid-cols-4">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="rounded-2xl border border-olive-100 bg-olive-50/60 p-4">
              <div className="h-3 w-24 animate-pulse rounded bg-olive-200" />
              <div className="mt-4 h-8 w-20 animate-pulse rounded bg-olive-200" />
              <div className="mt-3 h-3 w-32 animate-pulse rounded bg-olive-100" />
            </div>
          ))}
        </div>
      ) : statistics ? (
        <div className="space-y-5 p-4 sm:p-6">
          {statistics.hasClosedTrades ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              {metricCards.map((card) => (
                <article
                  key={card.label}
                  className="rounded-2xl border border-olive-100 bg-white p-4 shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive-500">
                    {card.label}
                  </p>
                  <p className={`mt-3 text-2xl font-bold ${card.tone}`}>{card.value}</p>
                  <p className={`mt-2 text-xs ${theme.textMuted}`}>{card.caption}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-olive-200 bg-white px-5 py-10 text-center">
              <p className={`text-base font-semibold ${theme.textPrimary}`}>
                {statistics.message || 'No closed trades for the selected period.'}
              </p>
              <p className={`mt-2 text-sm ${theme.textMuted}`}>
                Open positions and entries without realized exits are excluded from performance metrics.
              </p>
            </div>
          )}

          <div className="rounded-2xl border border-olive-100 bg-olive-50/40 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h4 className={`text-sm font-bold ${theme.textPrimary}`}>Cumulative Period PnL</h4>
                <p className={`text-xs ${theme.textMuted}`}>
                  Each step is one closed trade in this period. The line starts at zero and ends at Net PnL.
                </p>
                {statistics.hasClosedTrades && chartSeries.length > 0 && (
                  <p className={`mt-1 text-xs font-medium ${theme.textMuted}`}>
                    Final value {formatMetricNumber(chartSeries[chartSeries.length - 1]?.period_cumulative_pnl)} = Net
                    PnL {formatMetricNumber(statistics.totalPnl)}
                  </p>
                )}
              </div>
            </div>

            {statistics.chartData.length > 0 ? (
              <TradingChart
                data={chartSeries}
                title="Cumulative Period PnL"
                showStatsPanel={false}
                chartMode="profile"
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-olive-200 bg-white px-4 py-10 text-center">
                <p className={`text-sm font-semibold ${theme.textPrimary}`}>
                  {statistics.hasClosedTrades ? 'No realized PnL points yet' : 'No closed trades for the selected period.'}
                </p>
                <p className={`mt-2 text-xs ${theme.textMuted}`}>
                  {statistics.hasClosedTrades
                    ? 'Trades were found, but this period does not contain closed positions with realized PnL.'
                    : 'Select another period or wait for positions to close before evaluating performance.'}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="px-4 py-12 text-center sm:px-6">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-olive-100">
            <svg
              className="h-7 w-7 text-olive-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.7}
                d="M12 8v4m0 4h.01M4.93 19h14.14c1.48 0 2.4-1.6 1.67-2.87L13.67 4.87c-.74-1.27-2.6-1.27-3.34 0L3.26 16.13C2.53 17.4 3.45 19 4.93 19Z"
              />
            </svg>
          </div>
          <h4 className={`mt-4 text-base font-bold ${theme.textPrimary}`}>No statistics found</h4>
          <p className={`mt-2 text-sm ${theme.textMuted}`}>
            {showNoDataMessage && symbolCount > 0
              ? 'No statistics are available for the selected symbols and period.'
              : 'Select symbols to view statistics.'}
          </p>
        </div>
      )}
    </section>
  );
};

export default ProfileKPIStatistic;
